<template>
  <div class="onboard-page">
    <div v-if="isLoading" class="onboard-state">
      <i class="el-icon-loading onboard-state-icon"/>
      <p>正在加载入职邀请信息...</p>
    </div>

    <div v-else-if="inviteError" class="onboard-state is-error">
      <i class="el-icon-warning-outline onboard-state-icon"/>
      <p class="onboard-error-title">无法打开入职邀请</p>
      <p class="onboard-error-text">{{ inviteError }}</p>
    </div>

    <div v-else class="onboard-card">
      <div class="onboard-header">
        <div class="onboard-logo">ZAHACLUB</div>
        <h2 class="onboard-title">欢迎加入{{ storeDisplayName }}</h2>
        <p class="onboard-subtitle">职位：{{ invite.role_name }}</p>
        <div class="onboard-steps">
          <span class="step" :class="{ active: showForm || !hasApply }">1 填写资料</span>
          <i class="step-arrow">→</i>
          <span class="step" :class="{ active: hasApply && applyState === applyStates.review.value }">2 {{ reviewLabel }}</span>
          <i class="step-arrow">→</i>
          <span class="step" :class="{ active: hasApply && applyState === applyStates.passed.value }">3 正式入职</span>
        </div>
      </div>

      <div v-if="showForm">
      <el-form
        ref="form"
        :rules="rules"
        :model="form"
        label-position="top"
        class="onboard-form"
        :validate-on-rule-change="false"
      >
        <div class="form-section-title">基本资料</div>

        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" maxlength="14" show-word-limit clearable placeholder="请输入昵称（展示给同事）"/>
        </el-form-item>

        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" maxlength="17" clearable placeholder="请输入登录用户名">
            <i v-if="usernameChecking" slot="suffix" class="el-icon-loading username-loading-icon"/>
            <i v-else-if="usernameAvailable === false" slot="suffix" class="el-icon-circle-close username-error-icon"/>
            <i v-else-if="usernameAvailable === true" slot="suffix" class="el-icon-circle-check username-success-icon"/>
          </el-input>
          <div
            class="username-tip"
            :class="{
              'is-valid': usernameAvailable === true,
              'is-invalid': usernameAvailable === false,
            }"
          >
            <template v-if="usernameChecking">正在检查用户名是否可用…</template>
            <template v-else-if="usernameAvailable === false">{{ usernameError || '该用户名已被使用，请换一个' }}</template>
            <template v-else-if="usernameAvailable === true">用户名可用</template>
            <template v-else>3 ~ 17 位，字母、数字或符号，不含空格</template>
          </div>
        </el-form-item>

        <el-form-item label="登录密码" prop="user_password">
          <el-input v-model="form.user_password" type="password" maxlength="16" show-word-limit clearable placeholder="请输入登录密码"/>
          <div class="form-input-tip">8 ~ 16 位，至少含字母、数字、符号的任意两种，不含空格</div>
        </el-form-item>

        <div class="form-section-title">头像</div>

        <v-image-upload
          v-model="form.avatar"
          label="头像"
          prefix="administrator"
          ratio="1:1"
          prop-name="avatar"
          placeholder="建议使用清晰正面照"
          :public-token="token"
        />

        <div class="form-section-title">联系方式（必填）</div>

        <el-form-item label="手机号" prop="phone">
          <div class="phone-row">
            <el-input v-model="form.phone" maxlength="11" clearable placeholder="请输入 11 位手机号"/>
            <el-button :disabled="!canSendCode || smsCountdown > 0 || phoneChecking || phoneAvailable === false" :loading="isSendingSms" @click="sendSms">
              {{ smsCountdown > 0 ? smsCountdown + ' 秒后重发' : '发送验证码' }}
            </el-button>
          </div>
          <div v-if="smsCountdown > 0" class="phone-tip">验证码已发送至 {{ codeSentPhone }}，{{ smsCountdown }} 秒后可重新发送</div>
          <div v-else class="phone-tip" :class="{ 'is-valid': phoneAvailable === true, 'is-invalid': phoneAvailable === false }">
            <template v-if="phoneChecking">正在检查手机号是否已被使用…</template>
            <template v-else-if="phoneAvailable === false">{{ phoneError || '该手机号已被使用，请更换' }}</template>
            <template v-else-if="phoneAvailable === true">手机号可用</template>
            <template v-else>用于接收试课提醒、微信绑定与门店联系，提交前需完成短信验证</template>
          </div>
        </el-form-item>

        <el-form-item v-if="form.phone" label="短信验证码" prop="code">
          <el-input v-model="form.code" maxlength="6" clearable placeholder="6 位验证码"/>
        </el-form-item>

        <div class="wechat-auto-tip">
          <p class="wechat-tip-title">关于企业微信</p>
          <p>请下载企业微信App（在应用商店搜索“企业微信”即可）。</p>
          <p>通过审核后，系统会使用你填写的手机号自动邀请你加入公司企业微信，收到邀请后按提示同意即可。</p>
          <p v-if="invite.role_name">如果你一直没收到邀请，请联系邀请你的人协助处理。</p>
        </div>

        <div class="onboard-submit-row">
          <el-button
            class="onboard-submit"
            type="primary"
            :loading="isSaving"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            提交入职申请
          </el-button>
          <div class="onboard-agreement">提交后由{{ reviewLabel }}，审核通过即可登录系统</div>
        </div>
      </el-form>
      </div>

      <div v-else-if="hasApply" class="onboard-status">
        <template v-if="applyState === applyStates.review.value">
          <i class="el-icon-time status-icon status-icon-review"/>
          <h3 class="status-title">申请已提交</h3>
          <p class="status-desc">你的入职申请已提交，正在等待{{ reviewLabel }}。审核结果会以短信发送到你的手机，也会在本页面同步显示。</p>
        </template>
        <template v-else-if="applyState === applyStates.passed.value">
          <i class="el-icon-success status-icon status-icon-passed"/>
          <h3 class="status-title">已入职</h3>
          <p class="status-desc">恭喜，你已通过审核并完成入职！按下方指引完成准备，就可以开始上班啦。</p>
        </template>
        <template v-else-if="applyState === applyStates.rejected.value">
          <i class="el-icon-warning-outline status-icon status-icon-rejected"/>
          <h3 class="status-title">申请被驳回</h3>
          <p class="status-desc">{{ apply.remark ? '驳回原因：' + apply.remark + '。' : '很遗憾，本次申请未通过。' }}{{ inviteValid ? '如仍有入职意向，可在邀请有效期内重新填写申请，无需重新获取邀请链接。' : '如仍有入职意向，请联系邀请你的人重新获取邀请链接后再申请。' }}</p>
          <el-button v-if="inviteValid" class="status-action" type="primary" @click="startReapply">重新填写申请</el-button>
          <p v-else class="status-expired">邀请链接已失效，请联系邀请你的人重新获取邀请链接</p>
        </template>
        <template v-else-if="applyState === applyStates.revoked.value">
          <i class="el-icon-info status-icon status-icon-revoked"/>
          <h3 class="status-title">申请已撤回</h3>
          <p class="status-desc">该申请已撤回，如需再次申请，请重新填写并提交。</p>
          <el-button v-if="inviteValid" class="status-action" type="primary" @click="startReapply">重新填写申请</el-button>
          <p v-else class="status-expired">邀请链接已失效，请联系邀请你的人重新获取邀请链接</p>
        </template>
        <template v-else>
          <i class="el-icon-info status-icon"/>
          <h3 class="status-title">申请状态未知</h3>
          <p class="status-desc">请刷新状态或联系邀请你的人处理。</p>
        </template>

        <div class="status-info">
          <div class="status-row">
            <span class="status-label">门店</span>
            <span class="status-value">{{ apply.store_name | formatStoreName }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">职位</span>
            <span class="status-value">{{ apply.role_name }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">昵称</span>
            <span class="status-value">{{ apply.nickname }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">用户名</span>
            <span class="status-value">{{ apply.username }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">手机号</span>
            <span class="status-value">{{ apply.phone || '-' }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">提交时间</span>
            <span class="status-value">{{ apply.add_time | timeToYmdHi }}</span>
          </div>
          <div class="status-row">
            <span class="status-label">当前状态</span>
            <span class="status-value status-state">{{ applyStateName }}</span>
          </div>
        </div>

        <!-- 入职准备指引：待审核提前装企业微信；已通过展示三步准备（含后台地址复制），同时填补页面下方留白 -->
        <div v-if="applyState === applyStates.review.value" class="status-guide">
          <div class="guide-title">提前准备</div>
          <p class="guide-para">请在应用商店搜索“企业微信”下载安装。审核通过后，系统会用你填写的手机号自动发出加入邀请，收到后点同意即可。</p>
        </div>
        <div v-else-if="applyState === applyStates.passed.value" class="status-guide">
          <div class="guide-title">入职三步走</div>
          <div class="guide-step">
            <span class="guide-index">1</span>
            <div class="guide-body">
              <p class="guide-label">安装企业微信</p>
              <p class="guide-text">应用商店搜索“企业微信”安装，<a class="guide-link" href="https://work.weixin.qq.com/" target="_blank" rel="noopener">或点此打开官网</a>。系统已向 {{ apply.phone || '你填写的手机号' }} 发出加入邀请，收到后点同意即可。</p>
            </div>
          </div>
          <div class="guide-step">
            <span class="guide-index">2</span>
            <div class="guide-body">
              <p class="guide-label">登录后台系统</p>
              <div class="guide-url-row">
                <span class="guide-url">{{ adminUrl }}</span>
                <el-button size="mini" plain @click="copyText(adminUrl, '后台地址已复制')">复制</el-button>
              </div>
              <p class="guide-text">用本页面填写的用户名和密码登录，手机和电脑都能打开。</p>
            </div>
          </div>
          <div class="guide-step">
            <span class="guide-index">3</span>
            <div class="guide-body">
              <p class="guide-label">微信端激活</p>
              <p class="guide-text">在微信中打开“嘉禾舞社”小程序，用本手机号登录一次；并关注“嘉禾舞社”公众号，方便接收课程与排班提醒。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getInviteByToken, onboardApplySubmit, sendOnboardSms, checkOnboardUsername, checkOnboardPhone, onboardApplyStatus } from '@/api/personnel';
import { formatStoreName } from '@/filters';
import { applyStates, findApplyState } from '@/enums/applyStates';

const validateUsername = (rule, value, callback) => {
  const username = String(value || '');
  if (!username) return callback(new Error('请输入用户名'));
  if (username.length < 3 || username.length > 17) return callback(new Error('用户名需为 3 ~ 17 位'));
  if (/\s/.test(username)) return callback(new Error('用户名不能包含空格'));
  if (!/[a-zA-Z0-9]/.test(username)) return callback(new Error('用户名需包含字母或数字'));
  callback();
};

const validatePhone = (rule, value, callback) => {
  const phone = String(value || '');
  if (!phone) return callback(new Error('请输入手机号'));
  if (!/^1\d{10}$/.test(phone)) return callback(new Error('请输入正确的 11 位手机号'));
  callback();
};

const validatePassword = (rule, value, callback) => {
  const password = String(value || '');
  if (!password) return callback(new Error('请输入密码'));
  if (password.length < 8 || password.length > 16) return callback(new Error('密码需为 8 ~ 16 位'));
  if (/\s/.test(password)) return callback(new Error('密码不能包含空格'));
  const types = [
    /[a-zA-Z]/.test(password),
    /\d/.test(password),
    /[^\w\s]/.test(password),
  ].filter(Boolean).length;
  if (types < 2) return callback(new Error('密码需包含字母、数字、符号中的任意两种'));
  callback();
};

export default {
  name: 'Onboard',
  data: () => ({
    applyStates,
    token: '',
    isLoading: true,
    inviteError: '',
    invite: {},
    isSaving: false,
    isSendingSms: false,
    smsCountdown: 0,
    codeSentPhone: '',
    smsTimer: null,
    usernameChecking: false,
    usernameAvailable: null,
    usernameError: '',
    usernameCheckTimer: null,
    usernameCheckSeq: 0,
    phoneChecking: false,
    phoneAvailable: null,
    phoneError: '',
    phoneCheckTimer: null,
    phoneCheckSeq: 0,
    apply: null,
    isEditing: false,
    inviteValid: false,
    form: {
      nickname: '',
      username: '',
      user_password: '',
      phone: '',
      code: '',
      avatar: '',
    },
    rules: {
      nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }],
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { validator: validateUsername, trigger: 'blur' },
      ],
      user_password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { validator: validatePassword, trigger: 'blur' },
      ],
      phone: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { validator: validatePhone, trigger: 'blur' },
      ],
      code: [
        { required: true, message: '请输入短信验证码', trigger: 'blur' },
      ],
    },
  }),
  computed: {
    canSendCode() {
      return this.form.phone && this.form.phone.length === 11;
    },
    canSubmit() {
      const hasBasic = this.form.nickname && this.form.username && this.form.user_password;
      const phoneOk = this.form.phone && this.form.phone.length === 11 && this.form.code;
      return !!(hasBasic && phoneOk && !this.usernameChecking && this.usernameAvailable !== false
        && !this.phoneChecking && this.phoneAvailable !== false);
    },
    reviewLabel() {
      // 店长（2）及以上（含超管 1）需要总部审核；普通职位由门店审核
      const roleId = Number(this.invite.administrator_role_id);
      return roleId === 1 || roleId === 2 ? '总部审核' : '门店审核';
    },
    storeDisplayName() {
      // 库中 store_name 不带“店”字，展示时统一拼接（见 filters/formatStoreName）
      return formatStoreName(this.invite.store_name);
    },
    hasApply() {
      return !!(this.apply && this.apply.exists);
    },
    applyState() {
      return this.hasApply ? Number(this.apply.apply_state) : null;
    },
    showForm() {
      if (!this.hasApply) return true;
      if (this.isEditing) {
        return this.applyState === applyStates.rejected.value || this.applyState === applyStates.revoked.value;
      }
      return false;
    },
    applyStateName() {
      if (!this.hasApply) return '';
      const found = findApplyState(this.applyState);
      return found && found[0] ? found[0].name : '未知';
    },
    adminUrl() {
      // 本页就是后台的一个路由页，URL 前缀即后台访问地址
      return window.location.origin;
    },
  },
  watch: {
    'form.phone'(val) {
      if (val !== this.codeSentPhone) this.form.code = '';
      this.phoneAvailable = null;
      this.phoneError = '';
      this.cancelPhoneCheck();
      if (this.form.phone) this.checkPhoneSoon();
    },
    'form.username'() {
      this.usernameAvailable = null;
      this.usernameError = '';
      this.cancelUsernameCheck();
      if (this.form.username) this.checkUsernameSoon();
    },
  },

  beforeCreate() {
    let vd = document.getElementById('onboard-css');
    const style = `
html, body, #app {
    background-color: #12121c;
    min-height: 100%;
}
body {
    background-color: #12121c;
}
#header {
    display: none;
}
#section {
    display: block;
    padding-bottom: 0;
    min-height: 100vh;
    background-color: #12121c;
}
#menu {
    display: none;
}`;
    if (vd) {
      vd.innerHTML = style;
    } else {
      vd = document.createElement('style');
      vd.id = 'onboard-css';
      vd.innerHTML = style;
      document.head.appendChild(vd);
    }
  },

  async mounted() {
    this.token = this.$route.query.token || '';
    if (!this.token) {
      this.inviteError = '邀请链接不正确，请确认是邀请人发送的最新链接！';
      this.isLoading = false;
      return;
    }
    // 先查申请状态：已提交过则直接展示状态页（不受邀请过期/撤销影响）
    await this.loadApplyStatus();
    if (this.hasApply) {
      this.applySyncToInvite();
      this.isLoading = false;
      return;
    }
    const { isError, data } = await getInviteByToken(this.token);
    this.isLoading = false;
    if (isError) {
      this.inviteError = data.message || '邀请链接不正确，请确认是邀请人发送的最新链接！';
      return;
    }
    this.invite = data;
    this.inviteValid = true;
  },
  beforeDestroy() {
    if (this.smsTimer) clearInterval(this.smsTimer);
    this.smsTimer = null;
    this.cancelUsernameCheck();
    this.cancelPhoneCheck();
    const vd = document.getElementById('onboard-css');
    if (vd) vd.innerHTML = '';
  },
  methods: {
    async loadApplyStatus() {
      const { isError, data } = await onboardApplyStatus({ token: this.token });
      if (isError) return false;
      this.apply = data || null;
      return !!(this.apply && this.apply.exists);
    },
    applySyncToInvite() {
      this.inviteValid = !!this.apply.invite_valid;
      this.invite = {
        store_name: this.apply.store_name || '',
        administrator_role_id: this.apply.administrator_role_id,
        role_name: this.apply.role_name || '',
      };
    },
    startReapply() {
      this.isEditing = true;
      const apply = this.apply || {};
      this.form.nickname = apply.nickname || '';
      this.form.username = apply.username || '';
      this.form.phone = apply.phone ? String(apply.phone) : '';
      this.form.code = '';
      this.form.user_password = '';
      this.form.avatar = apply.avatar || '';
      this.codeSentPhone = '';
      this.checkUsernameSoon();
      this.checkPhoneSoon();
    },
    copyText(text, message = '已复制') {
      // navigator.clipboard 仅在 https 等安全上下文可用，iOS Safari 下降级用 execCommand
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(message))
          .catch(() => this.fallbackCopy(text, message));
        return;
      }
      this.fallbackCopy(text, message);
    },
    fallbackCopy(text, message) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const isCopied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (isCopied) {
        this.$message.success(message);
      } else {
        this.$message.error('复制失败，请长按地址手动复制');
      }
    },
    cancelUsernameCheck() {
      if (this.usernameCheckTimer) {
        clearTimeout(this.usernameCheckTimer);
        this.usernameCheckTimer = null;
      }
      this.usernameCheckSeq += 1;
      this.usernameChecking = false;
    },
    cancelPhoneCheck() {
      if (this.phoneCheckTimer) {
        clearTimeout(this.phoneCheckTimer);
        this.phoneCheckTimer = null;
      }
      this.phoneCheckSeq += 1;
      this.phoneChecking = false;
    },
    checkPhoneSoon() {
      this.cancelPhoneCheck();
      const phone = this.form.phone.trim();
      if (!phone) return;
      if (!/^1\d{10}$/.test(phone)) return;
      this.phoneChecking = true;
      this.phoneCheckTimer = setTimeout(async () => {
        const seq = ++this.phoneCheckSeq;
        const { isError, data } = await checkOnboardPhone({ token: this.token, phone });
        if (seq !== this.phoneCheckSeq) return;
        this.phoneChecking = false;
        this.phoneCheckTimer = null;
        if (isError) {
          // token 失效等前置错误：无法判定占用状态，只给行内提示，不阻断
          this.phoneAvailable = null;
          this.phoneError = (data && data.message) || '手机号检查失败，请稍后重试';
          return;
        }
        // 占用与否以后端 available 字段为准，不再嗅探错误文案（文案改动会静默失效）
        this.phoneAvailable = !!(data && data.available);
        this.phoneError = this.phoneAvailable ? '' : ((data && data.message) || '该手机号已被使用，请更换');
      }, 400);
    },
    checkUsernameSoon() {
      this.cancelUsernameCheck();
      const username = this.form.username.trim();
      if (!username) return;
      if (username.length < 3 || username.length > 17 || /\s/.test(username) || !/[a-zA-Z0-9]/.test(username)) return;
      this.usernameChecking = true;
      this.usernameCheckTimer = setTimeout(async () => {
        const seq = ++this.usernameCheckSeq;
        const { isError, data } = await checkOnboardUsername({ token: this.token, username });
        if (seq !== this.usernameCheckSeq) return;
        this.usernameChecking = false;
        this.usernameCheckTimer = null;
        if (isError) {
          // token 失效等前置错误：无法判定占用状态，只给行内提示，不阻断
          this.usernameAvailable = null;
          this.usernameError = (data && data.message) || '用户名检查失败，请稍后重试';
          return;
        }
        // 占用与否以后端 available 字段为准，不再嗅探错误文案（文案改动会静默失效）
        this.usernameAvailable = !!(data && data.available);
        this.usernameError = this.usernameAvailable ? '' : ((data && data.message) || '该用户名已被使用，请换一个');
      }, 400);
    },
    async sendSms() {
      if (!this.canSendCode || this.isSendingSms || this.smsCountdown > 0) return;
      if (this.phoneChecking) {
        this.$message.warning('正在检查手机号是否可用，请稍候');
        return;
      }
      if (this.phoneAvailable === false) {
        this.$message.warning('该手机号已被使用，请更换后再获取验证码');
        return;
      }
      this.isSendingSms = true;
      const { isError } = await sendOnboardSms({ phone: this.form.phone, token: this.token });
      this.isSendingSms = false;
      if (isError) return;
      this.$message.success('验证码已发送，请查收');
      this.codeSentPhone = this.form.phone;
      this.startSmsCountdown();
    },
    startSmsCountdown() {
      if (this.smsTimer) clearInterval(this.smsTimer);
      this.smsCountdown = 60;
      this.smsTimer = setInterval(() => {
        this.smsCountdown -= 1;
        if (this.smsCountdown <= 0) {
          clearInterval(this.smsTimer);
          this.smsTimer = null;
        }
      }, 1000);
    },
    handleSubmit() {
      if (this.isSaving) return;
      if (this.usernameChecking) {
        this.$message.warning('正在检查用户名是否可用，请稍候');
        return;
      }
      if (this.usernameAvailable === false) {
        this.$message.warning('该用户名已被使用，请更换后再提交');
        return;
      }
      if (this.phoneChecking) {
        this.$message.warning('正在检查手机号是否可用，请稍候');
        return;
      }
      if (this.phoneAvailable === false) {
        this.$message.warning('该手机号已被使用，请更换后再提交');
        return;
      }
      if (!this.form.phone || !/^1\d{10}$/.test(this.form.phone)) {
        this.$message.warning('请输入正确的 11 位手机号');
        return;
      }
      if (!this.form.code) {
        this.$message.warning('请先获取并输入短信验证码');
        return;
      }
      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isSaving = true;
        const data = {
          token: this.token,
          nickname: this.form.nickname,
          username: this.form.username,
          user_password: this.form.user_password,
          phone: this.form.phone || 0,
          code: this.form.code || 0,
          avatar: this.form.avatar,
        };
        const { isError } = await onboardApplySubmit(data);
        this.isSaving = false;
        if (isError) return;
        // 先本地切换为“待审核”状态，避免提交后短暂停留在可编辑表单
        this.apply = {
          exists: true,
          apply_state: applyStates.review.value,
          invite_valid: this.inviteValid,
          store_name: this.invite.store_name || '',
          administrator_role_id: this.invite.administrator_role_id,
          role_name: this.invite.role_name || '',
          nickname: this.form.nickname,
          username: this.form.username,
          phone: this.form.phone,
          remark: '',
          add_time: Math.floor(Date.now() / 1000),
        };
        this.isEditing = false;
        await this.loadApplyStatus();
        if (this.hasApply) {
          this.applySyncToInvite();
          this.$message.success('申请已提交，请等待审核');
        } else {
          this.$message.info('申请已提交，请等待审核');
        }
      });
    },
  },
};
</script>

<style scoped>
.onboard-page {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 48px 24px;
  box-sizing: border-box;
  background-color: #12121c;
  overflow-x: hidden;
}
.onboard-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background:
    linear-gradient(135deg, rgba(18, 18, 28, .92) 0%, rgba(42, 24, 78, .90) 48%, rgba(16, 34, 68, .92) 100%),
    url(https://img.jiahewushe.com/app/banner/mine-cover.jpg) center center / cover no-repeat;
}
.onboard-page::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 18% 16%, rgba(255, 45, 120, .20) 0, transparent 42%),
    radial-gradient(circle at 82% 82%, rgba(0, 229, 255, .16) 0, transparent 38%);
}

.onboard-card,
.onboard-state {
  position: relative;
  z-index: 1;
}

.onboard-card {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 36px 40px 30px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, .96);
  border-radius: 24px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, .38);
}

.onboard-state {
  width: 100%;
  max-width: 480px;
  padding: 44px 32px;
  box-sizing: border-box;
  text-align: center;
  color: #fff;
  background: rgba(255, 255, 255, .08);
  border: 1px solid rgba(255, 255, 255, .16);
  border-radius: 20px;
  -webkit-backdrop-filter: blur(10px);/* iOS Safari 需带前缀，否则毛玻璃不生效 */
  backdrop-filter: blur(10px);
}
.onboard-state.is-error {
  border-color: rgba(255, 110, 140, .4);
}
.onboard-state-icon {
  font-size: 42px;
  color: #ffd6e0;
  animation: onboard-loading 1.2s linear infinite;
}
.onboard-state.is-error .onboard-state-icon {
  animation: none;
  color: #ff9fb6;
}
.onboard-error-title {
  font-size: 18px;
  font-weight: 600;
  margin: 12px 0 8px;
  color: #ffe3ea;
}
.onboard-error-text {
  font-size: 13px;
  color: rgba(255, 255, 255, .75);
  line-height: 1.8;
  margin: 0;
}

.onboard-header {
  text-align: center;
  margin-bottom: 26px;
}
.onboard-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin: 0 auto 16px;
  padding: 10px;
  box-sizing: border-box;
  border-radius: 10px;
  line-height: 1;
  user-select: none;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
  color: #fff;
  font-size: 22px;
  font-weight: 900;
  letter-spacing: 2px;
  box-shadow: 0 8px 20px rgba(138, 43, 226, .26);
}
.onboard-title {
  margin: 0 0 6px;
  font-size: 24px;
  color: #1f2330;
}
.onboard-subtitle {
  margin: 0 0 14px;
  font-size: 14px;
  color: #7a8091;
}
.onboard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.onboard-steps .step {
  font-size: 12px;
  color: #98a0b3;
}
.onboard-steps .step.active {
  color: #ff2d78;
  font-weight: 600;
}
.onboard-steps .step-arrow {
  font-style: normal;
  font-size: 12px;
  color: #c7ccd8;
}

.onboard-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}
.onboard-form ::v-deep .el-form-item__label {
  padding: 0 0 6px;
  line-height: 1.4;
  font-size: 13px;
  color: #5a6072;
}
.onboard-form ::v-deep .el-input__inner,
.onboard-form ::v-deep .el-textarea__inner {
  height: 42px;
  line-height: 42px;
  border-radius: 8px;
  border-color: #dcdce8;
  background-color: #fafaff;
  color: #2f3445;
  caret-color: #8a2be2;
  transition: border-color .2s, box-shadow .2s, background-color .2s;
}
.onboard-form ::v-deep .el-input__inner::placeholder,
.onboard-form ::v-deep .el-textarea__inner::placeholder {
  color: #a6adc0;
}
.onboard-form ::v-deep .el-input__inner:hover {
  border-color: #b192e8;
}
.onboard-form ::v-deep .el-input.is-active .el-input__inner,
.onboard-form ::v-deep .el-input__inner:focus {
  border-color: #8a2be2;
  background-color: #fff;
  box-shadow: 0 0 0 2px rgba(138, 43, 226, .12);
}
.onboard-form ::v-deep .el-textarea__inner:focus {
  border-color: #8a2be2;
  box-shadow: 0 0 0 2px rgba(138, 43, 226, .12);
}
.onboard-form ::v-deep .el-input__count {
  color: #a6adc0;
}
.onboard-form ::v-deep .el-button {
  transition: color .2s, border-color .2s, background-color .2s, box-shadow .2s;
}
.onboard-form ::v-deep .phone-row .el-button {
  border-color: #dcdce8;
  color: #5a5f75;
  background: #fff;
}
.onboard-form ::v-deep .phone-row .el-button:hover,
.onboard-form ::v-deep .phone-row .el-button:focus {
  border-color: #b192e8;
  color: #8a2be2;
  background: #faf5ff;
}
.onboard-form ::v-deep .el-upload--text .el-upload-dragger {
  border: 1px dashed #d8d8ea;
  border-radius: 12px;
  background: #fbfbff;
}
.onboard-form ::v-deep .el-upload--text .el-upload-dragger:hover {
  border-color: #b192e8;
}
.onboard-form ::v-deep .el-upload--text .el-icon-upload,
.onboard-form ::v-deep .el-upload__text em {
  color: #8a2be2;
}
.onboard-form ::v-deep .el-upload__tip {
  color: #a6adc0;
}
.onboard-form ::v-deep .el-upload__tip .primary-color {
  color: #8a2be2;
  font-weight: 500;
}

.form-section-title {
  display: flex;
  align-items: center;
  margin: 22px 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #3a4051;
}
.form-section-title:first-of-type {
  margin-top: 0;
}
.form-section-title::before {
  content: '';
  width: 4px;
  height: 14px;
  margin-right: 8px;
  border-radius: 2px;
  background: linear-gradient(180deg, #ff2d78, #8a2be2);
}

.form-input-tip {
  font-size: 12px;
  line-height: 1.6;
  margin-top: 6px;
  color: #98a0b3;
}

.username-loading-icon {
  color: #909399;
}
.username-success-icon {
  color: #67c23a;
}
.username-error-icon {
  color: #f56c6c;
}
.username-tip {
  font-size: 12px;
  line-height: 1.6;
  margin-top: 6px;
  color: #98a0b3;
}
.username-tip.is-valid {
  color: #67c23a;
}
.username-tip.is-invalid {
  color: #f56c6c;
}

.phone-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.phone-row .el-input {
  flex: 1;
}
.phone-row .el-button {
  flex-shrink: 0;
}
.phone-tip {
  font-size: 12px;
  color: #98a0b3;
  line-height: 1.6;
  margin-top: 6px;
}
.phone-tip.is-valid {
  color: #67c23a;
}
.phone-tip.is-invalid {
  color: #f56c6c;
}

.wechat-auto-tip {
  margin: 2px 0 18px;
  padding: 10px 12px;
  background: #faf5ff;
  border: 1px solid #eadcfb;
  border-radius: 8px;
  font-size: 12px;
  color: #7a8091;
  line-height: 1.7;
}
.wechat-tip-title {
  font-weight: 600;
  color: #8a2be2;
  margin-bottom: 2px;
}
.wechat-auto-tip p {
  margin: 0;
}

.onboard-submit-row {
  margin-top: 10px;
}
.onboard-submit {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
}
.onboard-submit:hover,
.onboard-submit:focus {
  opacity: .9;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
}
.onboard-agreement {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: #98a0b3;
}

.onboard-status {
  padding: 8px 0 0;
  text-align: center;
}
.status-icon {
  display: block;
  font-size: 56px;
  margin-bottom: 12px;
}
.status-icon-review {
  color: #e6a23c;
}
.status-icon-passed {
  color: #67c23a;
}
.status-icon-rejected {
  color: #f56c6c;
}
.status-icon-revoked {
  color: #909399;
}
.status-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: #3a4051;
}
.status-desc {
  max-width: 430px;
  margin: 0 auto 18px;
  font-size: 13px;
  line-height: 1.8;
  color: #7a8091;
}
.status-action {
  height: 38px;
  padding: 0 28px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
  color: #fff;
}
.status-action:hover,
.status-action:focus {
  opacity: .9;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
}
.status-expired {
  margin-top: 8px;
  font-size: 12px;
  color: #f56c6c;
}
.status-info {
  margin-top: 20px;
  padding: 14px 16px;
  text-align: left;
  background: #fafaff;
  border: 1px solid #eceaf7;
  border-radius: 12px;
}
.status-row {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  font-size: 13px;
  color: #3a4051;
  border-bottom: 1px dashed #efedf8;
}
.status-row:last-child {
  border-bottom: none;
}
.status-label {
  flex-shrink: 0;
  color: #98a0b3;
}
.status-value {
  margin-left: 16px;
  text-align: right;
  word-break: break-all;
}
.status-state {
  font-weight: 600;
  color: #8a2be2;
}

.status-guide {
  margin-top: 16px;
  padding: 14px 16px;
  text-align: left;
  background: #faf5ff;
  border: 1px solid #eadcfb;
  border-radius: 12px;
}
.guide-title {
  font-size: 14px;
  font-weight: 600;
  color: #8a2be2;
  margin-bottom: 10px;
}
.guide-para {
  margin: 0;
  font-size: 12px;
  line-height: 1.8;
  color: #7a8091;
}
.guide-step {
  display: flex;
  margin-bottom: 12px;
}
.guide-step:last-child {
  margin-bottom: 0;
}
.guide-index {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff2d78, #8a2be2);
  color: #fff;
  font-size: 12px;
}
.guide-body {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
}
.guide-label {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #3a4051;
}
.guide-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.7;
  color: #7a8091;
}
.guide-link {
  color: #8a2be2;
}
.guide-url-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.guide-url {
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #eadcfb;
  border-radius: 6px;
  font-size: 12px;
  color: #3a4051;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes onboard-loading {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .onboard-page {
    padding: 24px 16px 40px;
    align-items: flex-start;
  }
  .onboard-card {
    padding: 26px 20px 22px;
    border-radius: 18px;
  }
  .onboard-logo {
    padding: 8px;
    border-radius: 8px;
    font-size: 18px;
    letter-spacing: 1.5px;
  }
  .onboard-title {
    font-size: 20px;
  }
  .onboard-steps {
    flex-wrap: wrap;
  }
  .phone-row {
    align-items: stretch;
  }
}

/*
 * 手机端真机样式修复：
 * static/css/common/common-phone.css 中 ≤767px 的全局 !important 规则
 * （label 宽 23vw、content margin-left 23vw、dragger 67vw x 38vw）是为老式
 * 「左侧标签」后台页写的，会误伤本页 label-position="top" 表单，导致
 * 输入框相对 label 右移、头像上传框（23vw + 67vw > 卡片内容宽）溢出白色卡片。
 * 这里仅在本页范围内反制，不改全局文件/共享组件，避免影响其它后台页面。
 */
@media only screen and (max-width: 767px) {
  /* 还原 top 布局 label 宽度，取消 content 的 23vw 强制左距 */
  .onboard-form ::v-deep .el-form-item__label {
    width: auto !important;
    padding-right: 0 !important;
  }
  .onboard-form ::v-deep .el-form-item__content {
    margin-left: 0 !important;
  }

  /* 上传框占满卡片内容宽（覆盖全局 67vw/38vw/.6vw 边框），高度与桌面端一致 */
  .onboard-form ::v-deep .el-upload {
    display: block;
    width: 100%;
  }
  .onboard-form ::v-deep .el-upload-dragger {
    width: 100% !important;
    height: 180px !important;
    box-sizing: border-box;
    border: 1px dashed #d8d8ea !important;
  }

  /* 已上传预览：67vw 图片与删除按钮放不下同一行（会溢出卡片），改为换行居中放置 */
  .onboard-form ::v-deep .preview-box {
    flex-wrap: wrap;
  }
  .onboard-form ::v-deep .icon-close-box {
    width: 100% !important;
    height: 44px !important;/* 覆盖组件内联的 250*ratio px 高度 */
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
